import { type ClientErrorEvent, RECENT_ERRORS } from "@esi/shared";
import { useSyncExternalStore } from "react";

/**
 * The tab's last reduced errors (spec `client-telemetry` "Recent errors stay readable by the
 * user"), filled whether telemetry is on or off, in memory only: never written to
 * `localStorage`, `sessionStorage` or IndexedDB. `in-app-feedback` shows them and lets the user
 * attach them.
 */
let errors: readonly ClientErrorEvent[] = [];
const listeners = new Set<() => void>();

export function pushRecentError(error: ClientErrorEvent): void {
  errors = [...errors, error].slice(-RECENT_ERRORS);
  for (const listener of listeners) {
    listener();
  }
}

/** Oldest first, at most `RECENT_ERRORS`. */
export function recentErrors(): readonly ClientErrorEvent[] {
  return errors;
}

export function subscribeRecentErrors(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The tab's recent errors, re-rendering when one is added. */
export function useRecentErrors(): readonly ClientErrorEvent[] {
  return useSyncExternalStore(subscribeRecentErrors, recentErrors, recentErrors);
}

/** Tests only. */
export function resetRecentErrors(): void {
  errors = [];
}
